import type { TaskTagAdminDto } from "@/api/workTasks";
import { Button } from "@/design-system/components/ui";
import {
  DataTable,
  type Column,
} from "@/components/layout/DataTable";
import { tagColorHex } from "./tagColors";
import { formatDateInTz, useDateFormatter } from "@/utils/datetime";

interface Props {
  rows: TaskTagAdminDto[];
  loading: boolean;
  error: string | null;
  onEdit: (tag: TaskTagAdminDto) => void;
  onMerge: (tag: TaskTagAdminDto) => void;
  onArchive: (tag: TaskTagAdminDto) => void;
  onRestore: (tag: TaskTagAdminDto) => void;
}

function relative(date: string, tz: string): string {
  const dt = new Date(date);
  const diffMs = Date.now() - dt.getTime();
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDateInTz(date, tz);
}

/**
 * Tasks v1.1 § 2.2 — admin list table. Per-row actions plumb back to
 * the parent page; archived rows are dimmed but still rendered when the
 * "Show archived" toggle is on.
 */
export function TagListTable({
  rows, loading, error, onEdit, onMerge, onArchive, onRestore,
}: Props) {
  const { dateTime, tz } = useDateFormatter();
  const columns: Column<TaskTagAdminDto>[] = [
    {
      key: "name",
      header: "Name",
      render: (t) => (
        <span className={t.isArchived ? "opacity-60" : undefined}>
          <span className="inline-flex items-center gap-space-2">
            <span
              aria-hidden
              className="inline-block h-3 w-3 rounded-full shrink-0"
              style={{ backgroundColor: tagColorHex(t.color) }}
            />
            <span className="font-semibold text-onyx">{t.name}</span>
            {t.isArchived && (
              <span className="text-label text-gray-mid">(archived)</span>
            )}
          </span>
        </span>
      ),
    },
    {
      key: "description",
      header: "Description",
      render: (t) => (
        <span
          className="text-small text-gray-mid"
          title={t.description ?? undefined}
        >
          {t.description && t.description.length > 60
            ? `${t.description.slice(0, 60)}…`
            : t.description ?? "—"}
        </span>
      ),
    },
    {
      key: "usage",
      header: "Usage",
      render: (t) => (
        <span className="text-small text-onyx tabular-nums">
          {t.totalUsageCount} ({t.openUsageCount} open)
        </span>
      ),
    },
    {
      key: "modified",
      header: "Last modified",
      render: (t) => (
        <span
          className="text-small text-gray-mid"
          title={dateTime(t.modifiedDate)}
        >
          {relative(t.modifiedDate, tz)}
        </span>
      ),
    },
    {
      key: "modifiedBy",
      header: "Modified by",
      render: (t) => (
        <span className="text-small text-onyx">
          {t.modifiedByDisplayName ?? "—"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (t) => (
        <div className="flex items-center gap-space-2 justify-end">
          {t.isArchived ? (
            <Button
              size="sm"
              variant="tertiary"
              type="button"
              onClick={() => onRestore(t)}
            >
              Restore
            </Button>
          ) : (
            <>
              <Button size="sm" variant="tertiary" type="button" onClick={() => onEdit(t)}>
                Edit
              </Button>
              <Button size="sm" variant="tertiary" type="button" onClick={() => onMerge(t)}>
                Merge into…
              </Button>
              <Button
                size="sm"
                variant="tertiary"
                type="button"
                onClick={() => onArchive(t)}
              >
                Archive
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      loading={loading}
      error={error}
      emptyMessage="No tags yet. Click 'New tag' to add one."
      rowKey={(t) => t.id}
    />
  );
}
